import type { FC, ReactNode } from 'react'

import { ArrowRight, BookOpen, CircleCheck, Flame, Play, Volume2 } from 'lucide-react'
import { ButtonLink } from '@/components/generals/button-link'
import { PhoneFrame } from '@/components/landing/phone-frame'
import { VideoDialog } from '@/components/landing/video-dialog'

import {
    HERO_BADGES,
    MAILTO_GESPREK,
    ROMAN_PHOTO
} from '@/constants/leesritme'

const HeroVisual: FC = (): ReactNode => {
    return (
        <div className='relative mx-auto w-full max-w-[36rem] lg:mx-0 lg:max-w-none'>
            <div aria-hidden='true' className='absolute inset-x-[4%] top-[8%] bottom-[6%] rounded-[3rem] bg-linear-to-br from-sun-soft via-coral-soft to-leaf-soft' />

            <svg aria-hidden='true' viewBox='0 0 400 400' className='absolute top-[-2%] left-1/2 w-[78%] -translate-x-1/2 text-coral/60'>
                <g fill='none' stroke='currentColor' strokeWidth='3' strokeLinecap='round'>
                    <path d='M200 18v34' />
                    <path d='M104 44l18 28' />
                    <path d='M296 44l-18 28' />
                    <path d='M40 112l30 16' />
                    <path d='M360 112l-30 16' />
                </g>
            </svg>

            <div className='relative h-[30rem] sm:h-[36rem] lg:h-[38rem]'>
                <PhoneFrame
                    src='/images/app/app-lezen.webp'
                    alt='Leesscherm met de tekst Een schip als sluisdeur over de Watersnoodramp, met een foto uit 1953'
                    eager
                    className='absolute top-[17%] left-[3%] hidden w-[40%] -rotate-[8deg] sm:block'
                />

                <PhoneFrame
                    src='/images/app/app-reeks.webp'
                    alt='Startscherm met de dagen van de week en een leesreeks van 6 dagen'
                    eager
                    className='absolute top-[22%] right-[3%] hidden w-[40%] rotate-[7deg] sm:block'
                />

                <PhoneFrame
                    src='/images/app/app-meelezen.webp'
                    alt='De app leest de tekst Een stad vol bijen voor en kleurt het woord dat wordt voorgelezen'
                    priority
                    className='absolute top-[6%] left-1/2 z-10 w-[62%] -translate-x-1/2 sm:w-[46%]'
                />

                <div className='float-soft absolute top-[10%] left-0 z-20 sm:top-[8%] sm:left-[2%]'>
                    <div className='flex items-center gap-3 rounded-2xl border border-ink/5 bg-white/95 py-2.5 pr-4 pl-2.5 shadow-lift backdrop-blur'>
                        <span className='grid size-10 place-items-center rounded-xl bg-sun-soft text-ink'>
                            <Volume2 className='size-5' aria-hidden='true' />
                        </span>

                        <span className='leading-tight'>
                            <span className='block text-sm font-bold text-ink'>Voorlezen</span>
                            <span className='block text-xs text-muted-foreground'>Elk woord kleurt mee</span>
                        </span>
                    </div>
                </div>

                <div className='float-soft absolute right-0 bottom-[24%] z-20 [animation-delay:-3s] sm:right-[1%]'>
                    <div className='flex items-center gap-3 rounded-2xl border border-ink/5 bg-white/95 py-2.5 pr-4 pl-2.5 shadow-lift backdrop-blur'>
                        <span className='grid size-10 place-items-center rounded-xl bg-coral-soft text-coral-deep'>
                            <Flame className='size-5' aria-hidden='true' />
                        </span>

                        <span className='leading-tight'>
                            <span className='block text-sm font-bold text-ink'>Leesreeks: 6 dagen</span>
                            <span className='block text-xs text-muted-foreground'>Elke dag lezen telt</span>
                        </span>
                    </div>
                </div>

                <div className='absolute bottom-[3%] left-0 z-20 sm:left-[4%]'>
                    <VideoDialog
                        label='Bekijk de video: Leesritme in vier minuten'
                        className='group flex items-center gap-3 rounded-full bg-ink py-2 pr-5 pl-2 text-left text-paper shadow-lift transition hover:bg-ink-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf focus-visible:ring-offset-2 focus-visible:ring-offset-paper'
                    >
                        <span className='grid size-11 place-items-center rounded-full bg-coral text-white transition group-hover:scale-105'>
                            <Play className='ml-0.5 size-5 fill-current' aria-hidden='true' />
                        </span>

                        <span className='leading-tight'>
                            <span className='block text-sm font-bold'>Bekijk de video</span>
                            <span className='block text-xs text-[#c3cad8]'>Leesritme in vier minuten</span>
                        </span>
                    </VideoDialog>
                </div>
            </div>
        </div>
    )
}

export const HeroSection: FC = (): ReactNode => {
    return (
        <section id='hero' className='relative isolate overflow-hidden'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10'>
                <div className='absolute -top-48 -right-40 size-[46rem] rounded-full bg-[radial-gradient(circle_at_center,rgb(246_207_93/0.35),transparent_62%)]' />
                <div className='absolute top-1/3 -left-56 size-[36rem] rounded-full bg-[radial-gradient(circle_at_center,rgb(29_106_91/0.10),transparent_65%)]' />
            </div>

            <div className='mx-auto grid max-w-7xl items-center gap-10 px-5 pt-8 pb-16 sm:px-8 lg:grid-cols-[1.02fr_1fr] lg:gap-8 lg:pt-12 lg:pb-24'>
                <div>
                    <p className='inline-flex items-center gap-2 rounded-full border border-leaf/15 bg-leaf-soft px-3.5 py-1.5 text-sm font-bold text-leaf'>
                        <BookOpen className='size-4' aria-hidden='true' />
                        Leesapp voor groep 1 tot en met 8
                    </p>

                    <h1 className='mt-6 font-heading font-soft text-[3.4rem] leading-[0.95] font-semibold tracking-[-0.025em] text-ink sm:text-7xl lg:text-[5.4rem]'>
                        Meer lezen.
                        <span className='mt-1 block font-medium text-leaf italic'>
                            Verder <span className='marker marker-animate'>groeien.</span>
                        </span>
                    </h1>

                    <p className='mt-7 max-w-xl text-xl leading-snug font-bold text-ink sm:text-[1.4rem]'>
                        Help kinderen meer leeskilometers maken, op school én thuis.
                    </p>

                    <p className='mt-4 max-w-xl text-lg leading-relaxed text-muted-foreground'>
                        Elke dag een rijke tekst van tien minuten in de klas, en thuis een tweede tekst over hetzelfde onderwerp. Meer lezen, minder nakijkwerk. Als aanvulling op je bestaande leesmethode of zaakvakken.
                    </p>

                    <div className='mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center'>
                        <ButtonLink href='#aanmelden' size='lg'>
                            Start gratis met je klas
                            <ArrowRight className='size-4' aria-hidden='true' />
                        </ButtonLink>

                        <a
                            href={MAILTO_GESPREK}
                            className='group inline-flex min-h-13 items-center gap-3 rounded-full border border-ink/15 bg-white/70 py-1.5 pr-5 pl-1.5 text-left transition hover:border-ink/30 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf focus-visible:ring-offset-2 focus-visible:ring-offset-paper'
                        >
                            <img src={ROMAN_PHOTO} alt='' width={40} height={40} className='size-10 rounded-full object-cover' />

                            <span className='leading-tight'>
                                <span className='block text-xs font-semibold text-muted-foreground'>Directeur of ib’er?</span>
                                <span className='block text-[0.9375rem] font-bold text-ink'>Plan 20 minuten met Roman</span>
                            </span>
                        </a>
                    </div>

                    <ul className='mt-8 grid max-w-xl gap-x-6 gap-y-2.5 sm:grid-cols-2'>
                        {HERO_BADGES.map(badge => (
                            <li key={badge} className='flex items-center gap-2 text-[0.9375rem] text-ink/80'>
                                <CircleCheck className='size-5 shrink-0 text-leaf' aria-hidden='true' />
                                {badge}
                            </li>
                        ))}
                    </ul>
                </div>

                <HeroVisual />
            </div>
        </section>
    )
}
